import { createContext, useContext } from 'react';

/** One discovered EHR field the section can be mapped onto. */
export interface ComputerUseNotePushFieldCandidate {
  /** Human label shown in the dropdown. */
  label: string;
  /** Opaque AxSelector wire JSON — kept as a raw string, never parsed. */
  selector: string;
}

/** The current mapping for a section heading. */
export interface ComputerUseNotePushSectionMapping {
  /** Selectors the section is mapped to (a user override wins over the stored hint). */
  selectors: string[];
  /** True when the mapping is a user override rather than the stored hint. */
  isOverride: boolean;
  /**
   * Human-readable label for the current target — the matching discovered
   * field's label, else the selector's own name.
   */
  label: string;
}

export interface ComputerUseNotePushMappingContextValue {
  /** Whether note-push mapping chips should render. */
  enabled: boolean;
  /** True when the mapping came from durable-store hydration. */
  isHydratedFromStore: boolean;
  /** FIELD candidates discovered on the target app, for the reassignment dropdown. */
  fieldCandidates: ComputerUseNotePushFieldCandidate[];
  /** The current mapping for a section heading, or `undefined` when unmapped. */
  getSectionMapping: (heading: string) => ComputerUseNotePushSectionMapping | undefined;
  /** Reassign a section to selectors (writes user-override candidate state only). */
  onReassignSection: (heading: string, selectors: string[]) => void;
}

export const ComputerUseNotePushMappingContext =
  createContext<ComputerUseNotePushMappingContextValue | null>(null);

/** The disabled context value — no provider mounted / no mapping. */
export const DISABLED_COMPUTER_USE_NOTE_PUSH_MAPPING: ComputerUseNotePushMappingContextValue =
  {
    enabled: false,
    isHydratedFromStore: false,
    fieldCandidates: [],
    getSectionMapping: () => undefined,
    onReassignSection: () => {},
  };

/**
 * Computer-use note-push mapping data for the section chip. Returns a disabled
 * default when no provider is mounted.
 */
export function useComputerUseNotePushMappingContext(): ComputerUseNotePushMappingContextValue {
  return (
    useContext(ComputerUseNotePushMappingContext) ??
    DISABLED_COMPUTER_USE_NOTE_PUSH_MAPPING
  );
}
